import React, { useState } from "react";
import { X, Mail, CheckCircle, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuthContext } from "@/lib/AuthProvider";
import { useToast } from "@/hooks/use-toast";

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onBackToLogin: () => void;
}

const ForgotPasswordModal = ({ isOpen, onOpenChange, onBackToLogin }: ForgotPasswordModalProps) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const { sendPasswordReset } = useAuthContext();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Directly attempt password reset - Firebase will validate internally
      await sendPasswordReset(email);
      setIsSuccess(true);
      toast({
        title: "Password Reset Email Sent",
        description: "If an account exists with this email, we've sent a password reset link.",
      });
    } catch (err: any) {
      console.error("Password reset error:", err);
      
      // Handle specific Firebase error codes
      switch (err.code) {
        case 'auth/user-not-found':
          // Don't reveal if user exists for security
          setIsSuccess(true);
          toast({
            title: "Password Reset Email Sent",
            description: "If an account exists with this email, we've sent a password reset link.",
          });
          break;
        case 'auth/invalid-email':
          setError("Please enter a valid email address");
          break;
        case 'auth/too-many-requests':
          setError("Too many requests. Please try again later");
          break;
        default:
          setError("Failed to send reset email. Please try again");
      }
      
      toast({
        title: "Password Reset Failed",
        description: "Please check your email address and try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setError("");
    setIsSuccess(false);
    onOpenChange(false);
  };

  const handleBackToLogin = () => {
    handleClose();
    onBackToLogin();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md w-full max-h-[90vh] p-0 gap-0 bg-white rounded-lg shadow-xl border-0 overflow-hidden flex flex-col">
        <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-10">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>

        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 bg-gradient-to-r from-jamaica-green to-jamaica-green/90">
          <DialogTitle className="text-2xl font-bold text-white text-center">
            {isSuccess ? "Check Your Email" : "Reset Password"}
          </DialogTitle>
          <DialogDescription className="text-white/90 text-center">
            {isSuccess 
              ? "We've sent you a password reset link"
              : "Enter your email address and we'll send you a link to reset your password"
            }
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 p-6">
          {isSuccess ? (
            <div className="space-y-6">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div className="space-y-2">
                  <p className="text-gray-700 font-medium">
                    Password reset email sent!
                  </p>
                  <p className="text-sm text-gray-600">
                    We've sent a password reset link to <strong>{email}</strong>
                  </p>
                  <p className="text-xs text-gray-500">
                    Check your spam folder if you don't see it in your inbox
                  </p>
                </div>
              </div>
              
              <div className="space-y-3">
                <Button
                  className="w-full bg-jamaica-green hover:bg-jamaica-green/90 text-white py-3"
                  onClick={handleBackToLogin}
                >
                  Back to Login
                </Button>
                <Button
                  variant="outline"
                  className="w-full py-3"
                  onClick={() => {
                    setIsSuccess(false);
                    setEmail("");
                  }}
                >
                  Send Another Email
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="resetEmail"
                  className="block text-base font-medium text-gray-700"
                >
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    type="email"
                    id="resetEmail"
                    placeholder="your@email.com"
                    className="pl-10 py-3"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-center text-red-600 text-sm bg-red-50 p-3 rounded-md">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-3 pt-2">
                <Button
                  type="submit"
                  className="w-full bg-jamaica-green hover:bg-jamaica-green/90 text-white py-3"
                  disabled={isLoading || !email}
                >
                  {isLoading ? "Sending..." : "Send Reset Link"}
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  className="w-full py-3"
                  onClick={handleBackToLogin}
                  disabled={isLoading}
                >
                  Back to Login
                </Button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal; 
